import { type QueryKey, skipToken, useQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Agents } from '@air/api';
import { type ListAgentSessionArtifactsResponse } from '@air/server-sdk';
import { callAirApi } from '@air/utils-api';

import { isWorkspaceAgentSessionUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionUnresolvableError';

export const PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST_KEY = 'PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST';
const ARTIFACT_TRANSIENT_RETRIES = 1;

export type PrivateWorkspaceAgentSessionArtifactsListParams = Omit<
  NonNullable<Parameters<typeof Agents.Sessions.Artifacts.list>[0]>,
  'agentType' | 'artifactIds' | 'cursor' | 'limit' | 'sessionId' | 'workspaceId'
> & {
  artifactIds?: readonly string[];
  pinned?: boolean;
  sessionId: string | undefined;
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentSessionArtifactsListKey = [
  typeof PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST_KEY,
  PrivateWorkspaceAgentSessionArtifactsListParams,
];

export const getPrivateWorkspaceAgentSessionArtifactsListKey = (
  params: PrivateWorkspaceAgentSessionArtifactsListParams,
): PrivateWorkspaceAgentSessionArtifactsListKey => [PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST_KEY, params];

export const isPrivateWorkspaceAgentSessionArtifactsListKey = (
  key: QueryKey,
): key is PrivateWorkspaceAgentSessionArtifactsListKey =>
  Array.isArray(key) && key[0] === PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST_KEY && isObject(key[1]);

export type UsePrivateWorkspaceAgentSessionArtifactsListOptions = {
  enabled?: boolean;
};

export const usePrivateWorkspaceAgentSessionArtifactsList = (
  params: PrivateWorkspaceAgentSessionArtifactsListParams,
  options?: UsePrivateWorkspaceAgentSessionArtifactsListOptions,
) => {
  const { artifactIds, pinned, sessionId, workspaceId } = params;
  const hasFilter = (artifactIds !== undefined && artifactIds.length > 0) || pinned !== undefined;
  const canFetch = Boolean(workspaceId) && Boolean(sessionId) && hasFilter;

  return useQuery<ListAgentSessionArtifactsResponse>({
    enabled: canFetch && (options?.enabled ?? true),
    queryFn:
      workspaceId && sessionId && hasFilter
        ? () =>
            callAirApi(() =>
              Agents.Sessions.Artifacts.list({
                agentType: 'workspace',
                ...(artifactIds && artifactIds.length > 0 ? { artifactIds: [...artifactIds] } : {}),
                ...(pinned !== undefined ? { pinned } : {}),
                sessionId,
                workspaceId,
              }),
            )
        : skipToken,
    queryKey: getPrivateWorkspaceAgentSessionArtifactsListKey(params),
    retry: (failureCount, error) =>
      !isWorkspaceAgentSessionUnresolvableError(error) && failureCount < ARTIFACT_TRANSIENT_RETRIES,
  });
};
